import { useMemo, useState } from 'react';

// Third-party
import { useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { useForm, FormProvider } from 'react-hook-form';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { mdiAsterisk } from '@mdi/js';
import Icon from '@mdi/react';
import { DateValidationError, TimeValidationError } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import { Moment } from 'moment/moment';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

// Components
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import {
  BaseDrawer,
  BaseDrawerTitle,
  BaseLabel,
  CustomSingleSelect,
} from '@mgm/common/components/base';
import {
  BASE_COLORS, getTimezone,
} from '@mgm/common/utils';
import {
  AvatarSelect,
} from '../../../../components/base';
import { InputDatePicker, InputTimePicker } from '../../../../components/pickers';

// Hooks
import { useAddScheduleShiftAPI, useEditScheduleShiftAPI } from '../../../../hooks/api/useSchedules';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useEditScheduleShift } from '../../Schedule/hooks';
import { usePermissions, useUserLocations } from '../../../../hooks/useCommon';

// Redux-toolkit
import { getIsOpenPagesNav } from '../../../../store/common/selectors';
import { commonActions } from '../../../../store/common/slice';
import { getMembers } from '../../../../store/team/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { schedulesActions } from '../../../../store/schedules/slice';

// Utils
import {
  processShiftTimes,
} from '../../../../utils/helpers';
import { TBreak, TScheduleShift } from '../../../../types';
import { BreaksCard } from '../../../../components/BreaksCard';
import { useShiftsValidation } from '../../../../hooks/useShiftsValidation';

const CreateEditScheduleShift = () => {
  const { mutate: addShiftMutate, isLoading: isAddShiftLoading } = useAddScheduleShiftAPI();
  const { mutate: editShiftMutate, isLoading: isEditShiftLoading } = useEditScheduleShiftAPI();

  const theme = useTheme() as TCustomTheme;

  const { isMobile } = useCustomTheme(theme);
  const isOpenPagesNav = useAppSelector(getIsOpenPagesNav);
  const members = useAppSelector(getMembers);
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['inputs', 'common', 'timesheets', 'schedule']);
  const { userLocations } = useUserLocations();
  const { permissions } = usePermissions();
  const [alert, setAlert] = useState({ open: false, message: '' });
  const { validateBreaks } = useShiftsValidation();

  const {
    formInputs,
    mode,
    handleFormChange,
    isEdited,
    setFormInputs,
  } = useEditScheduleShift();

  const methods = useForm({
    mode: 'all',
    values: {
      date: formInputs.date,
      startTime: formInputs.startTime,
      endTime: formInputs.endTime,
      location: formInputs.location,
      avatar: formInputs.employee,
    },
  });

  const validateTimes = (startTime: moment.Moment | null | undefined, endTime: moment.Moment | null | undefined) => {
    if (!startTime || !endTime) {
      setAlert({ open: true, message: t('bothStartAndEnd', { ns: 'timesheets' }) });
      return false;
    }

    if (!moment(endTime).isAfter(moment(startTime))) {
      setAlert({ open: true, message: t('endTimeMustBeAfter', { ns: 'timesheets' }) });
      return false;
    }

    setAlert({ open: false, message: '' }); // Close alert if validation passes
    return true;
  };

  const [isFormValid, setIsFormValid] = useState(true);

  const avatarMembers = useMemo(() => members.filter((member) => {
    const locationsIds = member.locations.map((location) => Number(location.id));
    const locationMatch = locationsIds.includes(formInputs.location?.id);

    if (!locationMatch) {
      return false;
    }

    return permissions.isManager
      ? member.permissionLevel === 'Employee' || member.permissionLevel === 'Manager'
      : true;
  }), [formInputs.location?.id, members, permissions.isManager]);

  const dataByMode = useMemo(() => {
    const isCreateMode = mode === 'create';
    return {
      title: isCreateMode ? t('createShift', { ns: 'timesheets' }) : t('editShift', { ns: 'timesheets' }),
      mainBtnText: isCreateMode ? t('addShift', { ns: 'timesheets' }) : t('editShift', { ns: 'timesheets' }),
      isCreateMode,
    };
  }, [mode]);

  const handleClose = () => {
    dispatch(commonActions.setIsOpenPagesNav(false));
    setTimeout(() => {
      dispatch(schedulesActions.setSelectedScheduleShift({} as TScheduleShift));
    }, 300);
  };

  const addOrEditShift = () => {
    const {
      position, avatarFile, name, email, department, locale,
    } = formInputs.employee;

    if (!validateTimes(formInputs?.startTime, formInputs?.endTime)) {
      methods.setError('endTime', {
        type: 'manual',
        message: 'End time must be after start  time',
      });
      setIsFormValid(false);
      return;
    }

    const results = processShiftTimes(formInputs.date, formInputs.startTime, formInputs.endTime);
    const breaks = formInputs.breaks?.map(((breakData) => {
      const breakResult = processShiftTimes(
        formInputs.date,
        moment(breakData.breakStart),
        moment(breakData.breakEnd),
      );

      return {
        ...breakData,
        breakStart: breakResult?.startUTC.format() as string,
        breakEnd: breakResult?.endUTC.format() as string,
      };
    }));

    const data = {
      schedulesData: {
        position,
        avatarFile,
        name,
        email,
        department,
        id: formInputs.id,
        location: formInputs.location,
        start: results?.startUTC.format() as string,
        end: results?.endUTC.format() as string,
        notes: [],
        tasks: [],
        timezone: getTimezone(),
        shopName: personnel.shopName,
        breaks,
      },
      locals: [{
        email,
        locale: locale || 'en',
      }],
    };

    if (dataByMode.isCreateMode) addShiftMutate(data);
    else editShiftMutate(data);
  };

  const addBreak = () => {
    setFormInputs((prevState) => ({
      ...prevState,
      breaks: [...prevState.breaks, {
        breakStart: '',
        breakEnd: '',
        id: prevState.breaks.length + 1,
      } as TBreak & { id: number }],
    }));
  };

  const onPickerError = (error: DateValidationError | TimeValidationError) => {
    if (error) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };

  const handleEndTime = (time: moment.Moment | null) => {
    handleFormChange('endTime', time);
    if (!validateTimes(formInputs.startTime, time)) {
      methods.setError('endTime', {
        type: 'manual',
        message: 'End time must be after start time',
      });
      setIsFormValid(false);
    } else {
      methods.clearErrors('endTime');
      setIsFormValid(true);
    }
  };

  const deleteBreak = (id?: number) => {
    setFormInputs((prevState) => ({
      ...prevState,
      breaks: prevState.breaks.filter((item) => item.id !== id),
    }));
  };

  const setBreakTime = (time: Moment | null, field: string, breakIndex: number) => {
    setFormInputs((prevState) => ({
      ...prevState,
      breaks: prevState.breaks?.map((breakItem, index) => (index === breakIndex ? {
        ...breakItem,
        [field]: time,
      } : breakItem)),
    }));
  };

  const isBtnDisabled = useMemo(() => {
    const isBreaksValid = formInputs.breaks?.length ? validateBreaks(formInputs as any) : true;

    return !methods.formState.isValid || !isFormValid || !isEdited || !formInputs.location?.id || !isBreaksValid || !formInputs.employee?.email;
  }, [formInputs, isEdited, isFormValid, methods.formState]);

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 600,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenPagesNav}
    >
      <BaseDrawerTitle title={dataByMode.title} handleClose={handleClose} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={methods.handleSubmit(addOrEditShift)}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid size={{
              lg: 12,
              md: 12,
              xs: 12,
              sm: 12,
            }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify width={34} sx={{ color: '#A855F7' }} icon="material-symbols:help-outline" />
                <Typography variant="subtitle2">
                  {t('membersAreFilteredBased', { ns: 'schedule' })}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{
              lg: 12,
              md: 12,
              xs: 12,
              sm: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid size={{
              lg: 6,
              md: 6,
              xs: 12,
              sm: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('location')}</BaseLabel>
                <Icon
                  color={BASE_COLORS.error}
                  className="mb-1 ml-1"
                  path={mdiAsterisk}
                  size={0.5}
                />
              </div>
              <CustomSingleSelect
                value={formInputs.location}
                name="location"
                isRequired
                options={userLocations}
                onChange={(location) => handleFormChange('location', location)}
              />
            </Grid>
            <Grid size={{
              lg: 6,
              md: 6,
              xs: 12,
              sm: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('member', { ns: 'common' })}</BaseLabel>
                <Icon
                  color={BASE_COLORS.error}
                  className="mb-1 ml-1"
                  path={mdiAsterisk}
                  size={0.5}
                />
              </div>
              <AvatarSelect
                onChange={(selectedMember) => handleFormChange('employee', selectedMember)}
                options={avatarMembers}
                isRequired
                value={formInputs.employee}
              />
            </Grid>
            <Grid size={{
              lg: 12,
              md: 12,
              xs: 12,
              sm: 12,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('date', { ns: 'timesheets' })}</BaseLabel>
                <Icon
                  color={BASE_COLORS.error}
                  className="mb-1 ml-1"
                  path={mdiAsterisk}
                  size={0.5}
                />
              </div>
              <InputDatePicker
                minDate={moment()}
                onChange={(date) => handleFormChange('date', date)}
                placeholder="Date"
                name="date"
              />
            </Grid>
            <Grid size={{
              lg: 6,
              md: 6,
              xs: 12,
              sm: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('shiftStart', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputTimePicker
                onChange={(time) => handleFormChange('startTime', time)}
                placeholder={t('startTime', { ns: 'timesheets' })}
                name="startTime"
                onError={onPickerError}
                isRequired
              />
            </Grid>
            <Grid size={{
              lg: 6,
              md: 6,
              xs: 12,
              sm: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('shiftEnd', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputTimePicker
                onChange={(time) => handleEndTime(time)}
                placeholder={t('endTime', { ns: 'timesheets' })}
                minTime={formInputs.startTime}
                name="endTime"
                isRequired
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 12, md: 12, lg: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <BreaksCard
              breaks={formInputs.breaks}
              addBreak={addBreak}
              startTime={formInputs.startTime}
              deleteBreak={deleteBreak}
              endTime={formInputs.endTime}
              setBreakTime={setBreakTime}
            />
            <Grid
              size={{
                lg: 12,
                xs: 12,
              }}
              paddingTop={4}
              paddingBottom={4}
            >
              <LoadingButton
                type="submit"
                size="large"
                variant="contained"
                loading={isAddShiftLoading || isEditShiftLoading}
                disabled={isBtnDisabled}
              >
                {dataByMode.mainBtnText}
              </LoadingButton>
            </Grid>
          </Grid>
          {alert.open && (
          <Box pt={4}>
            <Alert onClose={() => setAlert({ open: false, message: '' })} severity="error" sx={{ width: '100%' }}>
              {alert.message}
            </Alert>
          </Box>
          )}
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  CreateEditScheduleShift,
};
